import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Shield, Calendar, ShoppingBag, Users, IndianRupee, LogOut } from 'lucide-react';
import AdminMealPlans from '../components/AdminMealPlans';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState('bookings');
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!loading && !user) nav('/', { replace: true });
  }, [user, loading, nav]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [s, b, o] = await Promise.all([
          axios.get(`${API}/admin/stats`, { withCredentials: true }),
          axios.get(`${API}/admin/bookings`, { withCredentials: true }),
          axios.get(`${API}/admin/orders`, { withCredentials: true }),
        ]);
        setStats(s.data);
        setBookings(b.data);
        setOrders(o.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Access denied');
      }
    })();
  }, [user]);

  if (loading || !user) return <div className="min-h-screen bg-[#faf7f1] flex items-center justify-center"><div>Loading…</div></div>;

  if (err) {
    return (
      <div className="min-h-screen bg-[#faf7f1] flex items-center justify-center px-6">
        <div className="bg-white rounded-3xl p-10 max-w-md text-center shadow">
          <Shield className="w-12 h-12 text-[#c9743a] mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-[#1a2b17]">Not authorised</h2>
          <p className="text-[#4a5544] mt-2">{err}</p>
          <Button onClick={() => nav('/dashboard')} className="mt-6 rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white">Back to dashboard</Button>
        </div>
      </div>
    );
  }

  const cards = [
    { label: 'Bookings', value: stats?.bookings ?? 0, icon: Calendar, color: 'bg-[#e8f2e2] text-[#3a5a2c]' },
    { label: 'Orders', value: stats?.orders ?? 0, icon: ShoppingBag, color: 'bg-[#fce7d5] text-[#c9743a]' },
    { label: 'Users', value: stats?.users ?? 0, icon: Users, color: 'bg-[#e6e2f2] text-[#5b4e8a]' },
    { label: 'Revenue', value: `₹${stats?.revenue ?? 0}`, icon: IndianRupee, color: 'bg-[#f8e6c1] text-[#a07417]' },
  ];

  return (
    <div className="min-h-screen bg-[#faf7f1]">
      <header className="bg-[#1a2b17] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><Shield className="w-5 h-5" /> <span className="font-serif text-lg">NutrifyMe Admin</span></div>
          <div className="flex items-center gap-3">
            <div className="text-sm hidden md:block">{user.email}</div>
            <Button onClick={logout} variant="outline" className="rounded-full h-9 bg-transparent border-white/30 text-white hover:bg-white hover:text-[#1a2b17]"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-serif text-3xl md:text-4xl text-[#1a2b17] mb-6">Overview</h1>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.label} className="bg-white rounded-3xl p-6 shadow-sm">
              <div className={`w-11 h-11 rounded-2xl ${c.color} flex items-center justify-center mb-3`}><c.icon className="w-5 h-5" /></div>
              <div className="text-xs tracking-widest uppercase text-[#7a8471]">{c.label}</div>
              <div className="font-serif text-3xl text-[#1a2b17] mt-1">{c.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-white rounded-3xl shadow-sm overflow-hidden">
          <div className="flex border-b border-neutral-100">
            {['bookings', 'orders', 'meal plans'].map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`px-6 py-4 text-sm font-medium capitalize transition ${tab === t ? 'text-[#3a5a2c] border-b-2 border-[#3a5a2c]' : 'text-[#7a8471]'}`}>{t}</button>
            ))}
          </div>

          {tab === 'bookings' && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[#7a8471] text-xs tracking-widest uppercase">
                  <tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Phone</th><th className="p-4">Goal</th><th className="p-4">When</th><th className="p-4">Status</th></tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && <tr><td className="p-6 text-center text-[#7a8471]" colSpan={6}>No bookings yet.</td></tr>}
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t border-neutral-100 hover:bg-[#faf7f1]">
                      <td className="p-4 font-medium text-[#1a2b17]">{b.name}</td>
                      <td className="p-4">{b.email}</td>
                      <td className="p-4">{b.phone}</td>
                      <td className="p-4 max-w-xs truncate">{b.goal || '—'}</td>
                      <td className="p-4 text-[#7a8471]">{new Date(b.created_at).toLocaleString()}</td>
                      <td className="p-4"><span className="text-[10px] tracking-widest uppercase bg-[#fce7d5] text-[#c9743a] px-2 py-1 rounded-full">{b.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'orders' && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[#7a8471] text-xs tracking-widest uppercase">
                  <tr><th className="p-4">Order</th><th className="p-4">Customer</th><th className="p-4">Items</th><th className="p-4">Total</th><th className="p-4">Payment</th><th className="p-4">When</th></tr>
                </thead>
                <tbody>
                  {orders.length === 0 && <tr><td className="p-6 text-center text-[#7a8471]" colSpan={6}>No orders yet.</td></tr>}
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-neutral-100 hover:bg-[#faf7f1]">
                      <td className="p-4 font-mono text-xs">{o.id}</td>
                      <td className="p-4">{o.customer?.name}<br /><span className="text-xs text-[#7a8471]">{o.customer?.email}</span></td>
                      <td className="p-4">{o.items?.length}</td>
                      <td className="p-4 font-medium">₹{o.total}</td>
                      <td className="p-4"><span className="text-[10px] tracking-widest uppercase bg-[#e8f2e2] text-[#3a5a2c] px-2 py-1 rounded-full">{(o.payment?.status || 'PAID').replace('_', ' ')}</span></td>
                      <td className="p-4 text-[#7a8471]">{new Date(o.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'meal plans' && (
            <div className="p-6">
              <AdminMealPlans />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
